// src/pages/settings/create-organization/CreateOrganizationBillingPage.tsx
// [UI 2026-10-11] Checkout is a glass summary. The charge itself opens in Stripe.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Building2, Puzzle, Sparkles, Users } from 'lucide-react';
import { useTranslation } from '../../../contexts/languages/LanguageContext';
import OnboardingFrame from '../../misc/onboarding/OnboardingFrame';
import { publicJourney } from '../../misc/onboarding/continuousPath';
import { loadOnboardingPerson } from '../../misc/onboarding/OnboardingAccountPage';
import {
  clearHeldPassword,
  fetchPerSeatQuote,
  heldPassword,
  perSeatSubmission,
  startPerSeatCheckout,
  submitPerSeatOnboarding,
  type ApiRefusal,
  type PerSeatQuote,
} from '../../misc/onboarding/perSeatApi';
import { isStructureDraftReady, loadStructureDraft, saveStructureDraft } from '../../misc/onboarding/structureDraft';
import { PRICES_FAILED_LINE, usePriceBook } from '../../misc/onboarding/usePriceBook';
import { loadOrgSetup } from './orgSetup';
import { LINE_LABEL_KEYS, formatMxn, orderQuote } from './orderQuote';
import '../../misc/onboarding/builder.css';
import './orgFlow.css';
import '../../auth/auth.css';

// [HANDOFF] Which feature lines (indexes into settings.upgrade.plans.<id>.features) are listed on the plan summary card.
const INCLUDED: Record<string, number[]> = {
  free: [0, 1],
  base: [0, 1, 2, 3, 4],
  pro: [0, 1, 4, 5],
};

// [HANDOFF] A refusal's way back (self-stamp 83 (a); D1 THE ANSWERS): the line's link to the screen that fixes it - the plan for the
// structure over its limit, the extensions for an extension refused, the account step for its own fields (the account page's rule:
// the field the server names) - and the log-in beside an e-mail that already has an account (D5).
const LOGIN_URL = 'https://app.ventaje.com/login';
const LOGIN_CODES = ['EMAIL_TAKEN', 'ONBOARD_EMAIL_EXISTS'];
const ACCOUNT_WAY = { to: '/onboarding/cuenta', words: 'settings.createOrganization.checkoutFixAccount' };
const WAY_BACK: Record<string, { to: string; words: string }> = {
  STRUCTURE_OVER_LIMIT: { to: '/onboarding/plan', words: 'settings.createOrganization.checkoutFixPlan' },
  EXTENSION_INVALID: { to: '/onboarding/extensiones', words: 'settings.createOrganization.checkoutFixExtensions' },
  ORG_NAME_TAKEN: ACCOUNT_WAY,
  PASSWORD_WEAK: ACCOUNT_WAY,
};
const ACCOUNT_FIELDS = ['name', 'email', 'firstName', 'lastName', 'password'];

function wayBack(refusal: ApiRefusal): { to: string; words: string } | null {
  if (LOGIN_CODES.includes(refusal.code)) return null;
  return WAY_BACK[refusal.code] ?? (ACCOUNT_FIELDS.includes(refusal.field ?? '') ? ACCOUNT_WAY : null);
}

// The refusal's line is the server's own message (Spanish, S-6) unless perSeatApi made it - NETWORK_ERROR, ANSWER_MALFORMED or an
// HTTP status without a server message - when the checkout's failure line stands in (the account page's rule, D-C8).
function refusalText(refusal: ApiRefusal, failedLine: string): string {
  if (refusal.code === 'NETWORK_ERROR' || refusal.code === 'ANSWER_MALFORMED') return failedLine;
  return /^\/api\/\S+ answered \d+$/.test(refusal.message) ? failedLine : refusal.message;
}

// [HANDOFF] [SIGNUP-TOGETHER] LAST SCREEN OF THE TOGETHER ONBOARDING (/onboarding/checkout; WU-142 UNIT 7, docs/manifest/WU-142.md
// SESSION 32 RECORD UNIT 7 THE DESIGN D-C4 to D-C7). The server takes over here: the browser shows his figures and never prices the charge.
//
// What the screen reads:
//   structure  = loadStructureDraft()    -> the builder's draft; its applicationId is the server's once P2 has answered
//   setup      = loadOrgSetup()          -> { planId, cycle, addons } as the plan and extensions screens chose them
//   person     = loadOnboardingPerson()  -> { first, last, email } from ventaje_ob_person (S-11)
//   password   = heldPassword()          -> held in memory by the account step, never in storage (S-11); let go once P2 has answered
//   quote      = orderQuote(book, draft, setup) -> his figures from the book (S-9); null until the book is read
// The gold button (D-C4): with the password held, P2 (submitPerSeatOnboarding) stores the application and the draft keeps its id,
// then P4 (startPerSeatCheckout) opens Stripe Checkout and the browser goes there; no password and an id: P4 alone; neither: back to
// the account step for the password (D1). The discount box (D-C5): P3 (fetchPerSeatQuote) checks the code against the stored
// application (S-10 a) and its answer is what the summary shows (D-C6); P4 sends only a code P3 accepted. Already paid, or a state not pending (CHK4): the return page.
// [PRICING] Commissionable revenue for the partner program = quote.plan (seats only), NOT quote.total.
const CreateOrganizationBillingPage: React.FC = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const draft = loadStructureDraft();
  const setup = loadOrgSetup();
  // [PRICING] His figures come from the book (S-9), as the account page's do: null until the book is read or when a row it needs is missing.
  const { book, failed, retry } = usePriceBook();
  const quote = book === null ? null : orderQuote(book, draft, setup);
  const quoteMissing = book !== null && quote === null;
  const planId = quote === null ? setup.planId : quote.planId;
  const prepaid = quote !== null && quote.months > 1;
  const person = loadOnboardingPerson();
  const applicationId = draft.applicationId;

  useEffect(() => {
    if (!isStructureDraftReady(draft)) navigate('/onboarding', { replace: true });
  }, [draft, navigate]);
  // [HANDOFF] Discount code: "Aplicar" checks it with P3 against the stored application (S-10 a) - stored first by the gold button's
  // rule - and a valid code's line and total are the server's; a blank code clears it. P4 sends only the code P3 accepted (`applied`).
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState('');
  const [serverQuote, setServerQuote] = useState<PerSeatQuote | null>(null);
  const [failure, setFailure] = useState<ApiRefusal | null>(null);
  const [codeError, setCodeError] = useState('');
  const [busy, setBusy] = useState(false);
  const discount = serverQuote === null ? null : serverQuote.discount;
  const serverDue = serverQuote === null ? null : discount === null ? serverQuote.totalMxn : discount.totalAfterMxn;
  const hisDue = quote === null ? null : quote.dueNow;

  // THE SCREEN'S NUMBERS (D-C6): the account as stored is what P4 charges, so with no password held and an application stored the
  // server's quote is read on the page's load - the application stored then (loadedId), never the one P2 writes later in the flow;
  // with the password held his quote stands until P2 runs.
  const [loadedId] = useState(applicationId);
  useEffect(() => {
    if (heldPassword() !== undefined || loadedId === undefined) return;
    let live = true;
    fetchPerSeatQuote(loadedId).then((answer) => {
      if (!live) return;
      if (answer.ok) setServerQuote(answer.value);
      else setFailure(answer.refusal);
    });
    return () => {
      live = false;
    };
  }, [loadedId]);

  // SCREEN EQUALS CHARGE (S-9): when the server's total differs from his, the server's is shown and both are named here.
  useEffect(() => {
    if (serverQuote === null || hisDue === null || serverQuote.totalMxn === hisDue) return;
    console.warn(`[CHECKOUT] SCREEN_DIFFERS_FROM_SERVER: his quote ${hisDue} MXN, the server's ${serverQuote.totalMxn} MXN - the server's is shown`);
  }, [serverQuote, hisDue]);
  const money = (n: number) => formatMxn(n, language);
  const monthsText = quote === null ? '' : `${quote.months} ${t('settings.createOrganization.billMonths')}`;
  const cycleName = quote === null ? '' : t(`settings.upgrade.billing.${quote.cycle}`);

  // [HANDOFF] THE CALLS (D-C4, D-C5): one at a time - the box and both buttons wait while one runs.
  // The return page shows the state the server holds (P5): an application already paid arrives as Stripe's success URL carries
  // it; any other state the server will not check out (APPLICATION_STATE_INVALID: superseded, creation_failed) arrives with no
  // status, since nothing came back from Stripe (WU-142 UNIT 8 CHK4; docs/manifest/WU-142.md SESSION 45 RECORD, self-stamp 145).
  const toReturnPage = (id: string, paid: boolean) =>
    navigate(`/onboarding/estado?${paid ? 'status=success&' : ''}application_id=${encodeURIComponent(id)}`);

  /** A refusal's way: an application the server will not check out - already paid or not pending - goes to the return page; else its line. */
  const refuse = (refusal: ApiRefusal, id?: string) => {
    const paid = refusal.code === 'ACCOUNT_ALREADY_PAID' || refusal.code === 'CHECKOUT_ALREADY_PAID';
    const notPending = paid || refusal.code === 'APPLICATION_STATE_INVALID';
    const stateId = refusal.applicationId ?? id;
    if (notPending && stateId !== undefined) toReturnPage(stateId, paid);
    else setFailure(refusal);
  };

  /** The stored application (D1 THE SUBMIT'S TIMING): P2 when the password is held, the draft's id when not, else the account step. */
  const application = async (): Promise<string | null> => {
    const password = heldPassword();
    if (password === undefined) {
      if (applicationId !== undefined) return applicationId;
      navigate('/onboarding/cuenta');
      return null;
    }
    if (book === null) return null;
    const body = perSeatSubmission(book, draft, setup, person, password);
    if (body === null) return null;
    const answer = await submitPerSeatOnboarding(body);
    if (!answer.ok) {
      refuse(answer.refusal);
      return null;
    }
    saveStructureDraft({ ...draft, applicationId: answer.value.applicationId });
    clearHeldPassword();
    return answer.value.applicationId;
  };

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setFailure(null);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  const applyCode = () =>
    run(async () => {
      setCodeError('');
      const typed = code.trim();
      if (typed === '') {
        setApplied('');
        setServerQuote((held) => (held === null ? null : { ...held, discount: null }));
        return;
      }
      const id = await application();
      if (id === null) return;
      const answer = await fetchPerSeatQuote(id, typed);
      if (answer.ok) {
        setServerQuote(answer.value);
        if (answer.value.discount !== null) setApplied(answer.value.discount.code);
      } else if (answer.refusal.code === 'DISCOUNT_INVALID') {
        setCodeError(answer.refusal.message);
      } else {
        refuse(answer.refusal, id);
      }
    });

  const startCheckout = () =>
    run(async () => {
      const id = await application();
      if (id === null) return;
      const answer = await startPerSeatCheckout(id, applied === '' ? undefined : applied, `${window.location.origin}/onboarding/estado`);
      if (answer.ok) window.location.assign(answer.value.checkoutUrl);
      else refuse(answer.refusal, id);
    });

  // [SPACING] Summary = intro note, amount due (.org-due), then four glass cards (plan, company, sales, extras) in the .org-bill grid (orgFlow.css).
  // [PRICING] The plan card shows the seats only. Monthly: "seats x perSeat" = quote.plan.
  // 6 months / annual: "seats x monthly price x months" (planList), then "Descuento -NN%" (planDiscount), then the plan total (planDue).
  // The extras card lists each extension line (x months when paid up front) and their total (extrasDue).
  // The bar at the bottom shows quote.dueNow: "al mes" on monthly, "a pagar hoy" when paid up front.
  // WhatsApp-included is shown as a free row.
  // THE FAILED READ (S-9): no book or a row the quote needs missing -> the one line in place of the numbers; it is the retry.
  const summary = quote === null ? (
    failed || quoteMissing ? (
      <button type="button" className="ob-tag underline" onClick={retry}>
        {PRICES_FAILED_LINE}
      </button>
    ) : null
  ) : (
    <>
      {person.first ? (
        <p className="ob-flow-sub" style={{ textAlign: 'center' }}>{person.first} {person.last} · {person.email}</p>
      ) : null}
      <p className="ob-flow-note">{t('settings.createOrganization.checkoutLead')}</p>
      {/* The amount charged today sits above the cards; the discount code box is in the bottom bar between the two buttons. */}
      <div className="org-float-price org-due">
        <b className="tabular-nums">{money(serverDue ?? quote.dueNow)}</b>
        <span>{t(prepaid ? 'settings.createOrganization.dueNow' : 'settings.createOrganization.perMonth')}</span>
      </div>
      {discount === null ? null : (
        <div className="org-bill-row is-discount">
          <span>{t('settings.createOrganization.billDiscount')} · {discount.code}</span>
          <b className="tabular-nums">−{money(discount.amountOffMxn)}</b>
        </div>
      )}
      <div className="org-bill">
        <section className={`org-glass-card org-bill-card tone-${planId}`}>
          <h3 className="org-bill-head"><Sparkles size={18} />{t(`settings.upgrade.plans.${planId}.name`)}</h3>
          <p className="org-bill-include">{t(`settings.upgrade.plans.${planId}.description`)}</p>
          <ul className="org-bill-includes">
            {(INCLUDED[planId] ?? []).map((i) => (
              <li key={i}>{t(`settings.upgrade.plans.${planId}.features.${i}`)}</li>
            ))}
          </ul>
          {prepaid ? (
            <>
              <div className="org-bill-row">
                <span>{quote.seats} × {money(quote.listPerSeat)} × {monthsText}</span>
                <b className="tabular-nums">{money(quote.planList)}</b>
              </div>
              <div className="org-bill-row is-discount">
                <span>{t('settings.createOrganization.billDiscount')} · {cycleName} −{quote.discountPct}%</span>
                <b className="tabular-nums">−{money(quote.planDiscount)}</b>
              </div>
              <div className="org-bill-row is-total">
                <span>{t('settings.createOrganization.billPlanTotal')}</span>
                <b className="tabular-nums">{money(quote.planDue)}</b>
              </div>
            </>
          ) : (
            <div className="org-bill-row">
              <span>{quote.seats} × {money(quote.perSeat)}</span>
              <b className="tabular-nums">{money(quote.plan)}</b>
            </div>
          )}
        </section>
        <section className="org-glass-card org-bill-card">
          <h3 className="org-bill-head"><Building2 size={18} />{t('settings.createOrganization.billCompany')}</h3>
          {draft.path === 3 && <div className="org-bill-row"><span>{draft.empresas} {t('settings.createOrganization.companiesShort')}</span></div>}
          <div className="org-bill-row"><span>{draft.proyectos} {t('settings.createOrganization.projectsShort')}</span></div>
        </section>
        <section className="org-glass-card org-bill-card">
          <h3 className="org-bill-head"><Users size={18} />{t('settings.createOrganization.billSales')}</h3>
          <div className="org-bill-row"><span>{draft.equipos} {t('settings.createOrganization.teamsShort')}</span></div>
          <div className="org-bill-row"><span>{draft.asesores} {t('settings.createOrganization.advisorsShort')}</span></div>
          <div className="org-bill-row"><span>{draft.admins} {t('settings.createOrganization.adminsShort')}</span></div>
        </section>
        <section className="org-glass-card org-bill-card">
          <h3 className="org-bill-head"><Puzzle size={18} />{t('settings.createOrganization.billExtras')}</h3>
          <div className="org-bill-row"><span>{t('settings.createOrganization.whatsappIncluded')}</span></div>
          {quote.lines.map((line) => (
            <div key={line.id} className="org-bill-row">
              <span>{t(LINE_LABEL_KEYS[line.id])}{prepaid ? ` · ${money(line.amount)} × ${monthsText}` : ''}</span>
              <b className="tabular-nums">{money(line.amount * quote.months)}</b>
            </div>
          ))}
          {quote.lines.length > 0 ? (
            <div className="org-bill-row is-total">
              <span>{t('settings.createOrganization.billExtrasTotal')}</span>
              <b className="tabular-nums">{money(quote.extrasDue)}</b>
            </div>
          ) : null}
        </section>
      </div>
      {prepaid ? (
        <p className="ob-flow-note">
          {t('settings.createOrganization.prepaidNote')
            .replace('{months}', String(quote.months))
            .replace('{perMonth}', money(quote.total))}
        </p>
      ) : null}
    </>
  );

  const way = failure === null ? null : wayBack(failure);
  const foot = (
    <>
      {failure === null ? null : (
        <p className="auth-err">
          <AlertTriangle size={12} />
          {refusalText(failure, t('settings.createOrganization.checkoutFailed'))}
          {LOGIN_CODES.includes(failure.code) ? (
            <a className="ml-1 underline" href={LOGIN_URL}>{t('settings.createOrganization.accountEmailLogin')}</a>
          ) : null}
          {way === null ? null : (
            <Link className="ml-1 underline" to={way.to}>{t(way.words)}</Link>
          )}
        </p>
      )}
      <div className="org-float">
        <button
          type="button"
          className="ob-back"
          onClick={() => navigate('/onboarding/cuenta')}
        >
          {t('settings.createOrganization.back')}
        </button>
        <form
          className="org-code is-quiet org-code-foot"
          onSubmit={(event) => {
            event.preventDefault();
            applyCode();
          }}
        >
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={t('settings.createOrganization.discountPlaceholder')}
            aria-label={t('settings.createOrganization.discountLabel')}
            disabled={busy}
          />
          <button type="submit" disabled={busy}>{t('settings.createOrganization.discountApply')}</button>
          {codeError === '' ? null : <p className="auth-err"><AlertTriangle size={12} />{codeError}</p>}
        </form>
        {/* [HANDOFF] The gold button (D-C4): the application stored (P2) when the password is held, then Stripe Checkout (P4); held while a call runs and while the price read failed. */}
        <button
          type="button"
          className="btn-gold inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl text-sm font-bold"
          onClick={startCheckout}
          disabled={quote === null || busy}
        >
          {t('settings.createOrganization.stripeCheckout')}
        </button>
      </div>
    </>
  );

  const progress = publicJourney(draft.path, 'checkout');
  return (
    <OnboardingFrame step={progress.step} total={progress.total} band="card" foot={foot}>
      <div className="ob-flow-copy">
        <h1 className="ob-flow-title">{t('settings.createOrganization.checkoutTitle')}</h1>
        <p className="ob-flow-sub">{t('settings.createOrganization.checkoutSubtitle')}</p>
      </div>
      {summary}
    </OnboardingFrame>
  );
};

export default CreateOrganizationBillingPage;
